import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Warehouse View Component
 *
 * Fetches warehouse inventory data with batch/lot tracking and displays
 * it as a 5-6 level heterogeneous nested table.
 */
function WarehouseView({ onTableLoaded, setViewMode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const [levels, setLevels] = useState(0);
  const [totalFulfillable, setTotalFulfillable] = useState(0);

  const fetchWarehouseInventory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/warehouse/inventory`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch warehouse inventory');
      }

      const data = await response.json();

      setProductCount(data.product_count);
      setTotalFulfillable(data.total_fulfillable);
      setLevels(data.levels);
      setLoading(false);

      // Add the warehouse table to the main tables
      if (onTableLoaded) {
        onTableLoaded(data.table);
      }

      // Switch back to tables view to see the rendered table
      if (setViewMode) {
        setViewMode('tables');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="shopify-view">
      <div className="shopify-header">
        <h2>Warehouse Inventory - Batch/Lot Tracking</h2>
        <p className="shopify-description">
          Load warehouse inventory data with 5-6 levels of heterogeneous nesting showcasing batch tracking, FBA shipments, and movement history.
        </p>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={fetchWarehouseInventory}
            disabled={loading}
            className="btn-primary"
            style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', fontWeight: 'bold' }}
          >
            {loading ? 'Loading...' : 'Load Warehouse Inventory (5-6 Levels) 📦'}
          </button>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00'
          }}>
            <strong>Error:</strong> {error}
            <br />
            <small>
              Make sure the warehouse_state.json file exists in the warehouse_simulator directory
            </small>
          </div>
        )}

        {productCount > 0 && !loading && (
          <div className="shopify-stats" style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '6px'
          }}>
            <div style={{color: 'var(--text-success, green)', marginBottom: '0.5rem'}}>
              ✓ Warehouse inventory table imported successfully!
            </div>
            <div style={{fontSize: '0.9rem'}}>
              <strong>📦 {levels}-Level Heterogeneous Nested Table Created!</strong>
              <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                <li>Level 1: {productCount} Products (SKU, Name, Inventory Summary)</li>
                <li>Level 2: Inventory Summary (Fulfillable: {totalFulfillable.toLocaleString()}, Reserved, Available)</li>
                <li>Level 3: Batches (Lot Number, Manufacturer, Status, Quantities)</li>
                <li>Level 4: FBA Shipments (ID, Quantity, Ship Date, Warehouses)</li>
                <li>Level 5: Warehouse Locations (BFI4, MCE1, JFK8, etc.)</li>
                <li>Level 4-5: Movement History (Received, Shipped, Reserved, etc.)</li>
              </ul>
              <div style={{marginTop: '0.5rem', fontStyle: 'italic', fontWeight: 'bold', color: 'var(--accent, #3b82f6)'}}>
                This demonstrates deep nesting with variable-depth heterogeneous structures - different batches have different nested data!
              </div>
              <div style={{marginTop: '0.5rem'}}>
                Switch to "Tables" view to see it.
              </div>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-spinner" style={{
          textAlign: 'center',
          padding: '2rem',
          fontSize: '1.2rem',
          color: 'var(--text-secondary)'
        }}>
          Loading warehouse inventory data...
        </div>
      )}

      {!loading && productCount === 0 && !error && (
        <div className="empty-state" style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)'
        }}>
          <p>Click "Load Warehouse Inventory" to load batch/lot tracking data</p>
          <p style={{fontSize: '0.9rem', marginTop: '1rem'}}>
            The table will be added to your tables and you'll be automatically switched to the Tables view.
          </p>
          <p style={{fontSize: '0.85rem', marginTop: '1rem', fontStyle: 'italic'}}>
            Showcases 5-6 level nesting: Products → Inventory → Batches → Shipments/Movements → Warehouse Locations/Details
          </p>
        </div>
      )}
    </div>
  );
}

export default WarehouseView;
